import { usePermission } from '@realtyfeed/auth';
import { Typography } from '@realtyfeed/ui';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { requiredPathsFor } from '../config/route-permissions';

/**
 * Refuses a screen this session may not see.
 *
 * The second half of route-level gating: `PrivateRoute` answers "is anyone signed in",
 * this answers "may *this* session open *this* screen". Hiding the sidebar entry was never
 * enough — the URL still worked.
 *
 * ### It fails closed, but only once it knows
 *
 * While the session or its grant is unresolved `usePermission().isReady` is `false` and every
 * check denies, which is deliberate — it is how a caller tells "not yet" from "not allowed". So
 * this renders nothing until `isReady`, exactly as `PrivateRoute` does for the same reason: a
 * gate that treated "not yet checked" as "not allowed" would flash this page on every refresh.
 *
 * ### It does not redirect
 *
 * The legacy sent people to `/misc/not-authorized`, which loses the URL they were trying to open
 * and leaves them unable to say what they were denied. The page renders in place instead, so the
 * address bar still holds the answer to "what were you trying to do?".
 */
export const PermissionRoute = ({ children }: { children: ReactNode }): ReactNode => {
  const { isReady, canUseAnyPath } = usePermission();
  const { pathname } = useLocation();

  const required = requiredPathsFor(pathname);

  if (required.length === 0) return children;
  if (!isReady) return null;
  if (canUseAnyPath(required)) return children;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <Typography as="h2" size="2xl" weight="bold" className="text-ds-text-primary">
        You do not have access to this screen
      </Typography>
      <Typography as="p" size="base" className="max-w-md text-ds-text-secondary">
        Your account does not carry the permission this page needs. If you should have it, ask an
        administrator to add it to your role.
      </Typography>
      <Link
        to="/"
        className="inline-flex items-center justify-center rounded-lg bg-ds-action-primary px-4 py-2 text-sm font-semibold text-white hover:bg-ds-action-primary-hover"
      >
        Back to home
      </Link>
    </div>
  );
};
